"use client"

import { useState, useCallback } from "react"
import { createPortal } from "react-dom"
import { X, ChevronLeft, ChevronRight } from "lucide-react"
import { Z } from "@/lib/z-index"

interface CommentImagePreviewProps {
    images: string[]
}

/**
 * 评论图片展示 + 点击全屏查看
 * - 1 张图片：较大缩略图
 * - 2-3 张：横向排列
 * - 4-5 张：两行网格
 */
export function CommentImagePreview({ images }: CommentImagePreviewProps) {
    const [viewerIdx, setViewerIdx] = useState<number | null>(null)

    const openViewer = useCallback((idx: number) => setViewerIdx(idx), [])
    const closeViewer = useCallback(() => setViewerIdx(null), [])

    const goPrev = useCallback(() => {
        setViewerIdx((i) => (i !== null && i > 0 ? i - 1 : i))
    }, [])

    const goNext = useCallback(() => {
        setViewerIdx((i) => (i !== null && i < images.length - 1 ? i + 1 : i))
    }, [images.length])

    // 根据图片数量确定布局
    const count = images.length
    const gridClass =
        count === 1
            ? "grid-cols-1 max-w-[200px]"
            : count <= 3
                ? "grid-cols-3"
                : "grid-cols-3"

    return (
        <>
            <div className={`grid ${gridClass} gap-1.5 mt-2`}>
                {images.map((src, idx) => (
                    <button
                        key={src}
                        type="button"
                        onClick={() => openViewer(idx)}
                        className={`relative overflow-hidden rounded-lg border border-border/20 hover:border-border/40 transition-colors cursor-zoom-in ${count === 1 ? "aspect-[4/3] max-h-[180px]" : "aspect-square"
                            }`}
                    >
                        <img
                            src={src}
                            alt={`评论图片 ${idx + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                    </button>
                ))}
            </div>

            {/* 全屏图片查看器 */}
            {viewerIdx !== null && (
                <ImageViewer
                    images={images}
                    currentIdx={viewerIdx}
                    onClose={closeViewer}
                    onPrev={goPrev}
                    onNext={goNext}
                />
            )}
        </>
    )
}

/* ── 全屏图片查看器 ── */

interface ImageViewerProps {
    images: string[]
    currentIdx: number
    onClose: () => void
    onPrev: () => void
    onNext: () => void
}

function ImageViewer({ images, currentIdx, onClose, onPrev, onNext }: ImageViewerProps) {
    return createPortal(
        <>
            {/* 背景遮罩 */}
            <div
                className="fixed inset-0 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
                style={{ zIndex: Z.MODAL_OVERLAY }}
                onClick={onClose}
            />

            {/* 内容层 */}
            <div
                className="fixed inset-0 flex items-center justify-center p-4"
                style={{ zIndex: Z.MODAL_CONTENT }}
                onClick={onClose}
                onKeyDown={(e) => {
                    if (e.key === "Escape") onClose()
                    if (e.key === "ArrowLeft") onPrev()
                    if (e.key === "ArrowRight") onNext()
                }}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
            >
                {/* 关闭按钮 */}
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                    aria-label="关闭"
                >
                    <X className="h-5 w-5" />
                </button>

                {/* 上一张 */}
                {currentIdx > 0 && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onPrev() }}
                        className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                        aria-label="上一张"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                )}

                {/* 下一张 */}
                {currentIdx < images.length - 1 && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onNext() }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                        aria-label="下一张"
                    >
                        <ChevronRight className="h-5 w-5" />
                    </button>
                )}

                {/* 图片 */}
                <img
                    src={images[currentIdx]}
                    alt={`图片 ${currentIdx + 1}/${images.length}`}
                    className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl animate-in zoom-in-95 fade-in duration-200"
                    onClick={(e) => e.stopPropagation()}
                />

                {/* 计数器 */}
                {images.length > 1 && (
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/50 text-white text-xs tabular-nums">
                        {currentIdx + 1} / {images.length}
                    </div>
                )}
            </div>
        </>,
        document.body,
    )
}
